// 我的 › 分享給我（SPEC #org-model-shares、#partners-302 GET / DELETE /v1/me/shares）：別人逐台分享給我個人的設備——
// 分享者、設備、能做什麼、到期；不用接受，可以隨時移除（我不要了）。分享給我所屬組織的不在這裡（在組織的「分享給本組織」）。
import { useTranslation } from 'react-i18next';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useConfirm } from '../../components/ConfirmDialog';
import { useScope } from '../../lib/scope';
import { shareApi, type MyShare } from '../../lib/planeApi';
import { scopeLabel } from '../people/SharesTab';
import { errText } from './OrgSection';
import { dateStamp } from '../../lib/timefmt';

export function SharedWithMeSection() {
  const { t } = useTranslation();
  const { planes } = useScope();
  const { ask, dialog } = useConfirm();
  const qc = useQueryClient();
  const qs = useQueries({ queries: planes.map((p) => ({ queryKey: ['me-shares', p.api], queryFn: () => shareApi.mine(p.api), retry: 1 })) });
  const rows = qs.flatMap((q, i) => (q.data ?? []).filter((s) => s.status !== 'ended').map((s) => ({ s, api: planes[i].api })));
  const remove = useMutation({
    mutationFn: ({ api, s }: { api: string; s: MyShare }) => shareApi.removeMine(api, s.share_id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me-shares'] });
      void qc.invalidateQueries({ queryKey: ['me-devices'] });
    },
  });
  return (
    <section className="card" data-testid="shared-with-me">
      <h2 className="section-title">{t('mine.shared_title')}</h2>
      <p className="muted small">{t('mine.shared_hint')}</p>
      {qs.some((q) => q.isLoading) && <p className="muted">{t('common.loading')}</p>}
      {qs.some((q) => q.isError) && <p className="error">{t('scope.load_failed')}</p>}
      {remove.isError && <p className="error">{errText(t, remove.error)}</p>}
      {!qs.some((q) => q.isLoading) && rows.length === 0 && <p className="muted">{t('mine.shared_none')}</p>}
      <ul className="plain-list">
        {rows.map(({ s, api }) => (
          <li key={s.share_id} className="rel-card" data-my-share={s.share_id}>
            <div className="row">
              <strong>{s.sharer.display_name || t('shr.someone')}</strong>
              {s.status === 'suspended' && <span className="badge badge-warn">{t('svc.status_suspended')}</span>}
              <div className="spacer" />
              <span className="small muted">{s.expires_at ? t('ext.until', { date: dateStamp(s.expires_at) }) : t('shr.no_expiry')}</span>
              <button
                className="btn btn-sm"
                disabled={remove.isPending}
                onClick={async () =>
                  (await ask({ danger: true, title: t('mine.remove_confirm', { name: s.sharer.display_name || t('shr.someone') }), body: t('mine.remove_body'), action: t('mine.remove') })) &&
                  remove.mutate({ api, s })
                }
              >
                {t('mine.remove')}
              </button>
            </div>
            <div className="small">{s.devices.map((d) => d.display_name || d.device_ref).join(t('common.list_sep')) || '—'}</div>
            <div className="small muted">{s.scopes.map((x) => scopeLabel(t, x)).join(t('common.list_sep'))}</div>
          </li>
        ))}
      </ul>
      {dialog}
    </section>
  );
}
